<template>
    <div
        :id="containerId"
        :class="[containerClass, 'overflow-hidden']"
        :style="containerStyle"
        role="document"
        :aria-label="`PDF document: ${pdfTitle}`"
    ></div>
</template>

<script setup>
import { onMounted, onBeforeUnmount, useId, watch } from 'vue';

const props = defineProps({
    src: {
        type: String,
        required: true
    },
    pdfTitle: {
        type: String,
        default: 'Document'
    },
    containerClass: {
        type: String,
        default: ''
    },
    containerStyle: {
        type: [String, Object],
        default: ''
    },
    showDownload: {
        type: Boolean,
        default: false
    },
    showPrint: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['failed']);

const config = useRuntimeConfig();
const clientId = config.public.adobePdfEmbedClientId;

// Each instance needs a unique container id so multiple viewers can coexist
// on the same page (e.g. several attachments in AgendaViewer). useId() is
// stable across SSR/client which avoids hydration mismatches — Math.random()
// generates different values on the server vs the browser.
const containerId = `adobe-pdf-${useId()}`;

let adobeView = null;

const renderPdf = async () => {
    if (typeof window === 'undefined') return;
    if (!props.src) return;

    // Crash guard: any failure here (missing client ID, SDK script blocked,
    // license check failure, CORS rejection, corrupt PDF, etc.) emits 'failed'
    // so the parent component can fall back to the native iframe viewer.
    try {
        if (!clientId) {
            throw new Error('ADOBE_PDF_EMBED_CLIENT_ID is not set');
        }

        const ready = await loadAdobeSdk();
        if (!ready || !window.AdobeDC) {
            throw new Error('Adobe PDF Embed SDK failed to load');
        }

        const container = document.getElementById(containerId);
        if (!container) return;

        // Clear any previous render before re-instantiating (e.g. on src change).
        container.innerHTML = '';

        adobeView = new window.AdobeDC.View({
            clientId,
            divId: containerId
        });

        await adobeView.previewFile({
            content: { location: { url: props.src } },
            metaData: { fileName: props.pdfTitle }
        }, {
            embedMode: 'SIZED_CONTAINER',
            defaultViewMode: 'FIT_WIDTH',
            showDownloadPDF: props.showDownload,
            showPrintPDF: props.showPrint,
            showAnnotationTools: false,
            showLeftHandPanel: false,
            showFullScreen: true,
            showPageControls: true,
            dockPageControls: false
        });
    } catch (err) {
        console.warn('[Adobe PDF] render failed, falling back to native viewer:', err);
        emit('failed', err);
    }
};

onMounted(() => {
    renderPdf();
});

watch(() => props.src, () => {
    renderPdf();
});

onBeforeUnmount(() => {
    adobeView = null;
});
</script>
